/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined } from '@ant-design/icons'
import { Checkbox, Form, Input, Modal, Select, Switch } from 'antd'
import { Rule } from 'antd/es/form'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetTelePhonePrefixes } from '@hooks'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import useMgmts from '../../../../../hooks/useMgmt'
import { useUpdateSupplier } from '../../../../../hooks/useUpdateSupplier'
import { Supplier, TelephonePrefix } from '../../../../../types'
import { defaultTelephonePreffix } from '../../../../../constants'

const formValidations = {
  businessName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.businessName_format_require',
    min: 3,
    max: 20,
  },
  tradeName: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.tradeName_format_require',
    min: 3,
    max: 20,
  },
  email: { required: 'globals.obligatory_field', format: 'supplier_modal.email_format' },
  cellphone: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.cellphone_format_require',
    min: 10,
    max: 15,
  },
  sapId: {
    required: 'globals.obligatory_field',
    format: 'supplier_modal.sapId_format_require',
    min: 6,
    max: 6,
  },
}

const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const numericRegex = /^[0-9]*$/

type Props = {
  onClose: () => void
  selectedSupplier: number
  suppliersData: Supplier[]
}

const EditSupplierModal: React.FC<Props> = ({ onClose, selectedSupplier, suppliersData }) => {
  const [selectedPreffix, setSelectedPrefix] = useState<number>()
  const { t } = useTranslation()
  const { telephonePrefixes } = useGetTelePhonePrefixes()

  const { mgmts, mgmtsIsLoading } = useMgmts()

  const [form] = StyledForm.useForm()
  const { resetFields } = form

  const { updateSupplier } = useUpdateSupplier(onClose)

  const supplier = useMemo(
    () => suppliersData.find(({ id }) => id === selectedSupplier) ?? undefined,
    [suppliersData, selectedSupplier],
  )

  let dataSwitch: boolean
  if (supplier?.disableAt === null) {
    dataSwitch = true
  } else {
    dataSwitch = false
  }

  const [disableAtSwitch, setDisableAtSwitch] = useState<boolean>(dataSwitch)

  const validateEmail = (_rule: Rule, value: string) => {
    if (!value) {
      return Promise.reject(t(formValidations.email.required))
    }
    if (!regex.test(value)) {
      return Promise.reject(t(formValidations.email.format))
    }
    return Promise.resolve()
  }
  interface ValidationRule {
    validator: (_rule: Rule, value: string) => Promise<void>
  }

  const validateAlphanumeric: ValidationRule['validator'] = (_rule, value) => {
    if (!value || /^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s]+$/.test(value)) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(t('supplier_modal.format_require')))
  }

  const onOk = () => {
    form
      .validateFields()
      .then(() => {
        const formData = form.getFieldsValue()
        const { mgmtId, sapId, ...dataToSend } = formData
        updateSupplier(selectedSupplier, { ...dataToSend, disabled: !disableAtSwitch })
        // updateSupplier(selectedSupplier, { ...dataToSend, prefixId: selectedPreffix })
      })
      .catch(console.error)
  }

  const initialValues = useMemo(() => {
    if (supplier) {
      const { prefix, ...supplierData } = supplier
      return {
        ...supplierData,
        country: 'AR',
        mgmtId: supplier?.mgmt?.id,
      }
    }
    return undefined
  }, [supplier])

  useEffect(() => {
    if (!selectedPreffix && supplier) {
      if (supplier.prefix) {
        setSelectedPrefix(supplier.prefix.id)
      } else {
        const defaultPreffix = telephonePrefixes.find(
          ({ prefix }) => prefix === defaultTelephonePreffix,
        )
        setSelectedPrefix(defaultPreffix ? defaultPreffix.id : telephonePrefixes[0]?.id)
      }
    }
  }, [telephonePrefixes])

  return (
    <Modal
      open
      keyboard
      maskClosable
      onCancel={() => {
        resetFields()
        onClose()
      }}
      title={t('supplier_modal.edit_supplier')}
      closeIcon={<CloseOutlined />}
      okText={t('supplier_modal.button_save')}
      cancelText={t('supplier_modal.button_cancel')}
      onOk={onOk}
      destroyOnClose
    >
      <StyledForm form={form} initialValues={initialValues}>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="tradeName"
            label={t('supplier_modal.trade_name')}
            rules={[
              { validator: validateAlphanumeric },
              {
                min: formValidations.tradeName.min,
                max: formValidations.tradeName.max,
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row" style={{ display: 'none' }}>
          <StyledForm.Item
            name="country"
            label={t('supplier_modal.country')}
            rules={[]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="businessName"
            label={t('supplier_modal.business_name')}
            rules={[
              { validator: validateAlphanumeric },
              {
                min: formValidations.businessName.min,
                max: formValidations.businessName.max,
              },
            ]}
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="email"
            label={t('supplier_modal.email')}
            rules={[{ validator: validateEmail }]}
            required
            hasFeedback
          >
            <Input />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            name="cellphone"
            label={t('supplier_modal.cellphone')}
            rules={[
              {
                validator: (_, value) => {
                  // if (!value) {
                  //   return Promise.reject(t(formValidations.cellphone.required))
                  // }
                  if (!numericRegex.test(value)) {
                    return Promise.reject(t(formValidations.cellphone.format))
                  }
                  if (
                    value.length < formValidations.cellphone.min ||
                    value.length > formValidations.cellphone.max
                  ) {
                    return Promise.reject(
                      t(formValidations.cellphone.format, {
                        min: formValidations.cellphone.min,
                        max: formValidations.cellphone.max,
                      }),
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
            hasFeedback
          >
            <Input
              addonBefore={
                <Select
                  value={selectedPreffix}
                  style={{ width: 100 }}
                  onChange={value => setSelectedPrefix(value)}
                >
                  {telephonePrefixes?.map((item: TelephonePrefix) => (
                    <Select.Option key={item.prefix} value={item.id}>
                      {`+${item.prefix}${item.prefix === '54' ? ' (AR)' : ''}`}
                    </Select.Option>
                  ))}
                </Select>
              }
              onChange={e => {
                const enteredValue = e.target.value
                form.setFieldValue('cellphone', enteredValue)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row" justify="space-between" align="middle">
          <StyledForm.Item label={t('supplier_modal.internal_supplier')}>
            <Checkbox checked={!supplier?.sapId} disabled />
          </StyledForm.Item>
        </StyledRow>

        {supplier?.sapId && (
          <StyledRow className="carrier-form-row">
            <StyledForm.Item name="sapId" label={t('supplier_modal.Sap_code')} hasFeedback>
              <Input disabled />
            </StyledForm.Item>
          </StyledRow>
        )}

        <StyledRow className="carrier-form-row">
          <StyledForm.Item name="mgmtId" label={t('supplier_modal.zone')} hasFeedback>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder={t('supplier_modal.select_zone')}
              options={mgmts?.map(({ description, id }) => ({
                value: id,
                label: description,
              }))}
              loading={mgmtsIsLoading}
              optionFilterProp="label"
              disabled
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item label="Estado" name="disableAt">
            <Switch
              checked={disableAtSwitch}
              onChange={checked => {
                setDisableAtSwitch(checked)
              }}
              checkedChildren="HABILITADO"
              unCheckedChildren="DESHABILITADO"
            />
          </Form.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { EditSupplierModal }
